import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shield, Award, Users, Trophy, Swords, Landmark, Star, Medal } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { Loader, Empty, Card } from "../components/UI";
import { SimBadge } from "../components/Badges";
import { RecordsHeld } from "../components/RecordsHeld";

const CONTAINER = "mx-auto max-w-[1560px] px-4 sm:px-8 lg:px-12";

function CredStat({ label, value, Icon }) {
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4">
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-mono mb-1 flex items-center gap-1">{Icon && <Icon className="w-3 h-3 text-[#E5B842]" />}{label}</div>
      <div className="font-heading text-2xl font-black text-slate-50 tabular-nums">{value}</div>
    </div>
  );
}

export default function NationalTeamPage() {
  const { country } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/nations/${country}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [country]);

  if (!data) return <Loader label="Loading national team…" />;
  if (data.error) return <div className={`${CONTAINER} py-16 animate-fade-up`}><Empty label="National team not found." /></div>;

  const nt = data.national_team;
  const roster = data.roster;
  const r = data.rating || {};

  return (
    <div className="animate-fade-up pb-16 overflow-x-hidden">
      <div className={`${CONTAINER} pt-8`}>
        {/* NATIONAL TEAM CREDENTIAL */}
        <div data-testid="nt-profile" className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-6 sm:p-8 mb-6">
          <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(85% 130% at 90% 0%, rgba(212,175,55,0.12) 0%, transparent 55%)" }} />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-5">
            <span className="text-6xl sm:text-7xl leading-none shrink-0" aria-hidden="true"><Flag country={nt} width={64} /></span>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E5B842]">{nt.wsbf_nt_id}</div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-slate-50 leading-none mt-1 wsbf-slant-block break-words">{nt.name}</h1>
              {data.federation && (
                <Link to={`/federation/${data.federation.wsbf_fed_id}`} data-testid="nt-federation-link" className="inline-flex items-center gap-1.5 mt-2 text-sm text-slate-300 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                  <Landmark className="w-3.5 h-3.5 text-[#E5B842]" /> Represented by {data.federation.name}
                </Link>
              )}
            </div>
            <div className="text-left lg:text-right shrink-0">
              <div data-testid="nt-rating" className="font-heading text-4xl sm:text-5xl font-black text-[#E5B842] tabular-nums leading-none">{r.rating ?? "—"}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-1">Rating · {data.world_rank ? `World Rank #${data.world_rank.position}` : "Unranked"}</div>
              {r.algorithm_version && <div className="text-[9px] font-mono text-slate-700 mt-0.5">{r.algorithm_version} · RD ±{r.rd ?? "—"}</div>}
            </div>
          </div>
          <div className="relative mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <CredStat label="Record (W–D–L)" value={`${r.wins || 0}–${r.draws || 0}–${r.losses || 0}`} Icon={Shield} />
            <CredStat label="Matches" value={r.matches_count || 0} Icon={Award} />
            <CredStat label="Current Squad" value={roster?.count || 0} Icon={Users} />
            <CredStat label="World Rank" value={data.world_rank ? `#${data.world_rank.position}` : "—"} Icon={Star} />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* SQUAD — primary section */}
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-[#E5B842]" /> Current Squad</h2>
            {!roster || roster.members.length === 0 ? <Empty label="No roster published yet." /> : (
              <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] overflow-hidden" data-testid="nt-roster">
                <div className="px-5 py-2.5 bg-[#070707] text-[10px] font-mono uppercase tracking-wider text-slate-500 flex">
                  <span className="flex-1">Player</span>
                  <span className="w-16 text-center hidden sm:block">Pos</span>
                  <span className="w-20 text-right">Rating</span>
                  <span className="w-14 text-right">Caps</span>
                </div>
                {roster.members.map((m) => (
                  <div key={m.player_id} className="px-5 py-2.5 border-t border-[#2b2512]/60 flex items-center text-sm hover:bg-[#161616]/50" style={{ transition: "background-color 0.15s ease" }} data-testid="nt-roster-member">
                    <Link to={`/player/${m.wsbf_player_id}`} className="flex-1 flex items-center gap-2 text-slate-100 hover:text-[#E5B842] truncate" style={{ transition: "color 0.15s ease" }}>
                      {m.display_name}{m.is_simulated && <SimBadge />}
                    </Link>
                    <span className="w-16 text-center font-mono text-[11px] text-slate-500 hidden sm:block">{m.position || "—"}</span>
                    <span className="w-20 text-right font-mono text-slate-300">{m.rating ?? "—"}</span>
                    <span className="w-14 text-right font-heading font-bold text-[#E5B842]">{m.caps ?? 0}</span>
                  </div>
                ))}
                <div className="px-5 py-2 bg-[#070707] text-[10px] font-mono text-slate-600">Caps = official national-team appearances · Rating = player rating</div>
              </div>
            )}

            {/* RESULTS */}
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mt-8 mb-3 flex items-center gap-2"><Swords className="w-5 h-5 text-[#E5B842]" /> Results</h2>
            <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] overflow-hidden" data-testid="nt-results">
              {data.results.length === 0 ? <div className="p-5 text-slate-500 text-sm">No results yet.</div> :
                data.results.map((res) => (
                  <Link key={res.match_id} to={`/match/${res.match_id}`} className="flex items-center gap-2 px-4 sm:px-5 py-3 border-t border-[#2b2512]/60 first:border-t-0 text-sm hover:bg-[#161616]/60" style={{ transition: "background-color 0.15s ease" }}>
                    <span aria-hidden="true"><Flag country={res.home} width={20} /></span>
                    <span className="font-bold text-slate-200 w-12">{res.home?.short_name}</span>
                    <span className="font-heading font-black text-[#E5B842] tabular-nums w-16 text-center">{res.home_team_points}–{res.away_team_points}</span>
                    <span className="font-bold text-slate-200 w-12">{res.away?.short_name}</span>
                    <span aria-hidden="true"><Flag country={res.away} width={20} /></span>
                    <span className="ml-auto text-[9px] font-mono uppercase text-slate-600 hidden sm:inline">Team Points</span>
                  </Link>
                ))}
            </div>
          </div>

          {/* SIDE COLUMN */}
          <div className="space-y-6">
            <RecordsHeld records={data.records_held} testid="nt-records-held" />

            {data.honours && data.honours.length > 0 && (
              <div>
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#E5B842]" /> Honours</h2>
                <Card className="p-4 space-y-3" data-testid="nt-honours">
                  {data.honours.map((h, i) => (
                    <div key={i} data-testid="nt-honour-row" className="flex items-center gap-3">
                      <Medal className="w-5 h-5 text-[#E5B842] shrink-0" />
                      <div>
                        <div className="font-heading font-bold uppercase text-slate-100 text-sm">{h.title}</div>
                        <div className="text-xs text-slate-500 font-mono">{h.year}</div>
                      </div>
                    </div>
                  ))}
                </Card>
              </div>
            )}

            {data.rivalries && data.rivalries.length > 0 && (
              <div data-testid="nt-rivalries">
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2"><Swords className="w-5 h-5 text-[#E5B842]" /> Rivalries</h2>
                <Card className="p-2">
                  {data.rivalries.slice(0, 8).map((rv) => (
                    <Link key={rv.opponent.slug} to={rv.rivalry_link} data-testid={`nt-rivalry-${rv.opponent.slug}`}
                      className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#161616]/60 text-sm" style={{ transition: "background-color 0.15s ease" }}>
                      <span className="text-slate-200">vs {rv.opponent.name}</span>
                      <span className="font-mono text-[10px] uppercase text-slate-500">{rv.meetings} meetings →</span>
                    </Link>
                  ))}
                </Card>
              </div>
            )}

            <div>
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50 mb-3">Top Caps</h2>
              <Card className="p-4" data-testid="nt-top-caps">
                {data.top_caps.length === 0 ? <div className="text-slate-500 text-sm">No caps earned yet.</div> :
                  data.top_caps.map((c) => (
                    <div key={c.player_id} className="flex items-center justify-between py-1.5 text-sm">
                      <Link to={`/player/${c.wsbf_player_id}`} className="text-slate-200 hover:text-[#E5B842] truncate" style={{ transition: "color 0.15s ease" }}>{c.display_name}</Link>
                      <span className="font-heading font-bold text-[#E5B842]">{c.caps} caps</span>
                    </div>
                  ))}
              </Card>
            </div>

            <div>
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50 mb-3">Top Contributors</h2>
              <Card className="p-4" data-testid="nt-top-contributors">
                {data.top_contributors.length === 0 ? <div className="text-slate-500 text-sm">No contributions yet.</div> :
                  data.top_contributors.map((c) => (
                    <div key={c.player.player_id} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="text-slate-200 truncate">{c.player.display_name}</span>
                      <span className="font-mono text-slate-400">{c.team_points_generated} <span className="text-[10px] text-slate-600 uppercase">TP generated</span></span>
                    </div>
                  ))}
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
